import { AwsS3, Uppy } from 'uppy';
import { Controller } from '@hotwired/stimulus';
import { Turbo } from '@hotwired/turbo-rails';
import i18n from '../init/i18n';
import { guardNavigation } from '../helpers/navigation_guard';
import { showToast } from '../helpers/toast';

const MAX_FILES = 50;

export default class extends Controller {
  static targets = ['overlay'];
  static outlets = ['import-window'];
  static values = {
    allowedTypes: Array,
    importUrl: String,
    notifyUrl: String,
    parentFolderId: String,
    recordType: String
  };

  initialize() {
    this.depth = 0;
    this.imports = [];
    this.importQueue = Promise.resolve();
    this.importing = false;
    this.cancelled = false;
  }

  connect() {
    this.releaseNavigationGuard = guardNavigation({
      shouldGuard: () => this.importing,
      message: i18n.t('dataroom_dropzone.leave_confirm'),
      onLeave: () => this.cancel()
    });

    this.uppy = new Uppy({
      autoProceed: true,
      restrictions: {
        allowedFileTypes: this.allowedTypesValue,
        maxFileSize: 100 * 1024 * 1024
      },
      locale: {
        strings: {
          youCanOnlyUploadFileTypes: i18n.t('dataroom_dropzone.invalid_file_type', { types: '%{types}' }),
          exceedsSize: i18n.t('dataroom_dropzone.file_too_large', { file: '%{file}', size: '%{size}' })
        }
      }
    }).use(AwsS3, { companionUrl: '/', limit: 5 });

    this.uppy.on('upload-progress', (file) => this.#markLoading(file));
    this.uppy.on('upload-success', (file) => this.#enqueueImport(file));
    this.uppy.on('complete', () => this.#finishImport());
    this.uppy.on('upload-error', (file) => this.#markResult(file, false));
  }

  dragEnter(event) {
    if (!this.#hasFiles(event) || this.importing) return;
    event.preventDefault();
    this.depth++;
    this.#toggleDragging(true);
  }

  dragOver(event) {
    if (!this.#hasFiles(event)) return;
    event.preventDefault(); // required to stay a drop target
  }

  dragLeave(event) {
    if (!this.#hasFiles(event)) return;
    // depth counter absorbs enter/leave fired by child elements: only hide
    // once we've truly left the zone (as many leaves as enters).
    this.depth--;
    if (this.depth <= 0) {
      this.depth = 0;
      this.#toggleDragging(false);
    }
  }

  async drop(event) {
    if (!this.#hasFiles(event)) return;
    event.preventDefault();
    if (this.importing) return;

    this.depth = 0;
    this.#toggleDragging(false);
    this.shownErrors = new Set();
    this.importing = true;
    this.cancelled = false;

    const entries = this.#readTopLevelEntries(event);
    const resources = this.#topLevelResources(event, entries);
    const files = await this.#collectFiles(event, entries);

    if (files.length > MAX_FILES) {
      this.#showError(i18n.t('dataroom_dropzone.too_many_files', { smart_count: MAX_FILES }));
      this.importing = false;
      return;
    }

    this.#countFilesPerResource(resources, files);
    this.#flagMergedResources(resources);

    if (this.hasImportWindowOutlet) this.importWindowOutlet.open(resources);

    files.forEach(({ file, relativePath, resourceId }) => this.#addFile(file, relativePath, resourceId));

    // Every file rejected on add (bad type/size) → Uppy never fires 'complete',
    // so finish here or the window would stay stuck on "in progress".
    if (this.uppy.getFiles().length === 0) this.#endImport();
  }

  cancel() {
    if (!this.importing) return;

    this.cancelled = true;
    this.uppy.cancelAll();
    if (this.hasImportWindowOutlet) this.importWindowOutlet.markCancelled();
    this.#finishCancel();
  }

  disconnect() {
    this.releaseNavigationGuard?.();
    this.uppy?.close();
  }

  // private

  #toggleDragging(dragging) {
    this.overlayTarget.classList.toggle('d-lg-flex', dragging);
    this.element.classList.toggle('dragging', dragging);
  }

  #readTopLevelEntries(event) {
    return Array.from(event.dataTransfer.items || [])
      .map((item) => item.webkitGetAsEntry?.())
      .filter((entry) => entry && !this.#isHiddenFile(entry.name));
  }

  // Fallback for browsers without the entries API: flat files, no folders.
  #flatFiles(event) {
    return Array.from(event.dataTransfer.files).filter((file) => !this.#isHiddenFile(file.name));
  }

  #topLevelResources(event, entries) {
    if (entries.length === 0) {
      return this.#flatFiles(event).map((file, id) => ({ id, name: file.name, isDirectory: false }));
    }

    return entries.map((entry, id) => ({ id, name: entry.name, isDirectory: entry.isDirectory }));
  }

  async #collectFiles(event, entries) {
    if (entries.length === 0) {
      return this.#flatFiles(event).map((file, id) => ({ file, relativePath: '', resourceId: id }));
    }

    const counter = { files: 0 };
    const collected = await Promise.all(
      entries.map((entry, id) =>
        this.#collectEntry(entry, '', counter).then((files) => files.map((file) => ({ ...file, resourceId: id })))
      )
    );
    return collected.flat();
  }

  async #collectEntry(entry, path, counter) {
    if (entry.isFile) {
      const file = await new Promise((resolve) => entry.file(resolve));
      return [{ file, relativePath: path }];
    }

    if (counter.files > MAX_FILES) return [];

    const childPath = `${path}${entry.name}/`;
    const children = await this.#readAllEntries(entry.createReader(), counter);
    const nested = await Promise.all(children.map((child) => this.#collectEntry(child, childPath, counter)));
    return nested.flat();
  }

  async #readAllEntries(reader, counter) {
    const batch = await new Promise((resolve) => reader.readEntries(resolve));
    if (!batch.length || counter.files > MAX_FILES) return [];

    const visible = batch.filter((entry) => !this.#isHiddenFile(entry.name));
    counter.files += visible.filter((entry) => entry.isFile).length;
    return visible.concat(await this.#readAllEntries(reader, counter));
  }

  #addFile(file, relativePath, resourceId) {
    try {
      this.uppy.addFile({
        name: file.name,
        type: file.type,
        data: file,
        meta: { relativePath, resourceId }
      });
    } catch (error) {
      if (this.hasImportWindowOutlet) {
        this.importWindowOutlet.failResource(resourceId, error?.message, this.#fileLabel(file.name, relativePath));
      }
    }
  }

  #enqueueImport(file) {
    const done = this.importQueue.then(() => this.#onUploadSuccess(file));

    this.importQueue = done.catch(() => {});
    this.imports.push(done);
  }

  async #onUploadSuccess(file) {
    if (this.cancelled) return;

    const uploadedFileData = {
      id: file.meta.key?.replace(/^cache\//, ''),
      storage: 'cache',
      metadata: {
        size: file.size,
        filename: file.name,
        mime_type: file.type
      }
    };

    const body = new FormData();
    body.append('file', JSON.stringify(uploadedFileData));
    body.append('relative_path', file.meta.relativePath || '');
    body.append('parent_folder_id', this.parentFolderIdValue);
    body.append('type', this.recordTypeValue);

    try {
      const response = await fetch(this.importUrlValue, {
        method: 'POST',
        headers: { 'X-CSRF-Token': this.#csrfToken(), Accept: 'text/vnd.turbo-stream.html' },
        body
      });

      if (response.ok) {
        Turbo.renderStreamMessage(await response.text());
        this.#markResult(file, true);
      } else {
        this.#markResult(file, false, await this.#responseError(response));
      }

      return response;
    } catch (error) {
      this.#markResult(file, false);
      throw error;
    }
  }

  #markLoading(file) {
    if (this.hasImportWindowOutlet) this.importWindowOutlet.startResource(file.meta.resourceId);
  }

  #markResult(file, success, message) {
    if (!this.hasImportWindowOutlet) return;

    const label = this.#fileLabel(file.name, file.meta.relativePath);

    if (success) {
      this.importWindowOutlet.completeFile(file.meta.resourceId, label);
    } else {
      this.importWindowOutlet.failResource(file.meta.resourceId, message, label);
    }
  }

  // Only the import service answers with a JSON error — anything else (403, 500)
  // renders HTML and falls back to the window's generic message.
  async #responseError(response) {
    try {
      const { error } = await response.json();

      return error;
    } catch {
      return null;
    }
  }

  #countFilesPerResource(resources, files) {
    resources.forEach((resource) => { resource.total = 0; });
    files.forEach(({ resourceId }) => { resources[resourceId].total += 1; });
  }

  #flagMergedResources(resources) {
    const existingFolders = new Set(
      Array.from(this.element.querySelectorAll('[data-existing-folder-name]'))
        .map((el) => el.dataset.existingFolderName)
    );

    resources.forEach((resource) => {
      resource.merged = resource.isDirectory && existingFolders.has(resource.name);
    });
  }

  async #finishImport() {
    if (this.cancelled) return;

    const results = await Promise.allSettled(this.imports.splice(0));
    if (this.cancelled) return;

    this.#endImport();
    this.#notifyImport(results);

    this.uppy.clearUploadedFiles();
  }

  async #finishCancel() {
    const results = await Promise.allSettled(this.imports.splice(0));

    this.#notifyImport(results);
    this.importing = false;
    this.uppy.clearUploadedFiles();
  }

  #endImport() {
    if (this.hasImportWindowOutlet) this.importWindowOutlet.markDone();

    this.importing = false;
  }

  #notifyImport(results) {
    const recordIds = results
      .filter((r) => r.status === 'fulfilled' && r.value?.ok)
      .map((r) => r.value.headers.get('X-Record-Id'))
      .filter(Boolean);

    if (recordIds.length === 0) return;

    const body = new FormData();
    body.append('type', this.recordTypeValue);
    recordIds.forEach((id) => body.append('record_ids[]', id));

    fetch(this.notifyUrlValue, {
      method: 'POST',
      headers: { 'X-CSRF-Token': this.#csrfToken(), Accept: 'application/json' },
      body
    });
  }

  // helpers

  #showError(message) {
    if (this.shownErrors.has(message)) return;
    this.shownErrors.add(message);

    showToast(message);
  }

  // Path of the file inside the dropped folder ("Sous-dossier/rapport.pdf"): the
  // first segment is the dropped folder itself, already shown as the row name.
  #fileLabel(name, relativePath) {
    const nestedPath = (relativePath || '').split('/').slice(1).join('/');

    return `${nestedPath}${name}`;
  }

  #hasFiles(event) {
    return Array.from(event.dataTransfer?.types || []).includes('Files');
  }

  // Hidden OS files (.DS_Store & co) ride along inside dropped folders and would be
  // rejected by the MIME allowlist — filtered at every read boundary.
  #isHiddenFile(name) {
    return name.startsWith('.');
  }

  #csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }
}
